import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import RfidUploadedErrorTable from "./RfidUploadedErrorTable";
import { RFID_BULK_UPLOAD_HEADER_LABELS } from "./rfidBulkUploadColumns";
import { useRfidBulkUploadStore } from "./useRfidBulkUploadStore";
import "./RfidUploadedErrorTable.css";

const MAX_FILE_SIZE_MB = 10;

const RfidBulkUpload = ({ onSaved }) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const {
    incorrectData,
    isSaving,
    isBulkUploadLoading,
    isSampleDownloading,
    bulkUploadStep,
    showBulkUploadModal,
    bulkUploadRfid,
    saveRfidBulkUpload,
    downloadRfidSampleFile,
    closeBulkUploadModal,
  } = useRfidBulkUploadStore();

  const hasErrors = Array.isArray(incorrectData) && incorrectData.length > 0;

  useEffect(() => {
    if (showBulkUploadModal && bulkUploadStep === "form") {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [showBulkUploadModal, bulkUploadStep]);

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    closeBulkUploadModal();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleDownloadSample = async () => {
    await downloadRfidSampleFile();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please choose a file to upload");
      return;
    }

    const isExcel =
      selectedFile.name.endsWith(".xls") ||
      selectedFile.name.endsWith(".xlsx");
    if (!isExcel) {
      toast.error("Upload only .xls or .xlsx files");
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File size must be under ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    await bulkUploadRfid(selectedFile);
  };

  const handleSave = async () => {
    const result = await saveRfidBulkUpload();
    if (result?.success) {
      onSaved?.();
    }
  };

  if (bulkUploadStep === "form") {
    return (
      <div className="rfid-bulk-upload-popup">
        <div className="rfid-bulk-upload-form">
          <label className="form-label" htmlFor="rfid-bulk-file">
            Upload RFID File
          </label>
          <div className="rfid-bulk-upload-file-row">
            <input
              id="rfid-bulk-file"
              type="file"
              ref={fileInputRef}
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="form-control"
            />
            <button
              type="button"
              className="rfid-bulk-sample-link"
              onClick={handleDownloadSample}
              disabled={isSampleDownloading}
            >
              {isSampleDownloading
                ? "Downloading..."
                : "Download Sample RFID Format"}
            </button>
          </div>

          <div className="rfid-bulk-upload-instructions">
            <strong>Instructions:</strong>
            <ul>
              <li>Upload only .xls or .xlsx files (Max {MAX_FILE_SIZE_MB}MB)</li>
              <li>
                Required columns (same order as report/table):{" "}
                {RFID_BULK_UPLOAD_HEADER_LABELS.join(", ")}
              </li>
              <li>Use the sample format for correct column headers</li>
              <li>Do not leave required fields empty</li>
            </ul>
          </div>
        </div>

        <div className="rfid-bulk-upload-footer">
          <button
            type="button"
            className="rfid-bulk-footer-btn"
            onClick={handleClose}
            disabled={isBulkUploadLoading}
          >
            Close
          </button>
          <button
            type="button"
            className="rfid-bulk-footer-btn"
            onClick={handleUpload}
            disabled={isBulkUploadLoading}
          >
            {isBulkUploadLoading ? "Uploading..." : "Upload RFID"}
            {!isBulkUploadLoading && (
              <i className="fa-solid fa-paper-plane ms-2"></i>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rfid-bulk-upload-popup">
      {hasErrors ? (
        <>
          <div className="rfid-bulk-upload-warning" role="alert">
            <i className="fa-solid fa-circle-exclamation"></i>
            <span>
              Please check the errors below and re-upload the corrected file.
            </span>
          </div>
          <RfidUploadedErrorTable data={incorrectData} />
        </>
      ) : (
        <div className="rfid-bulk-upload-success">
          <div className="rfid-bulk-upload-success-icon">
            <i className="fa-solid fa-check"></i>
          </div>
          <p className="rfid-bulk-upload-success-title">No errors found</p>
          <p className="rfid-bulk-upload-success-text">
            You can proceed and save the uploaded data.
          </p>
        </div>
      )}

      <div className="rfid-bulk-upload-actions">
        <button
          type="button"
          className="rfid-bulk-btn rfid-bulk-btn-close"
          onClick={handleClose}
          disabled={isSaving}
        >
          Close
        </button>
        <button
          type="button"
          className="rfid-bulk-btn rfid-bulk-btn-save"
          onClick={handleSave}
          disabled={hasErrors || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default RfidBulkUpload;
